import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Calendar, MapPin, Clock, Plus, Pencil, Trash2, X, Link2, ExternalLink, Image } from "lucide-react";
import { toast } from "sonner";
import { API_URL as API } from "../../config";

const empty = { title: "", description: "", date: "", time: "", location: "", image_url: "" };

export default function AdminEventsPage() {
  const { authFetch } = useAuth();
  const { t, lang } = useLanguage();
  const a = t.admin;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/api/admin/events`);
    if (res.ok) setEvents(await res.json());
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editing ? `${API}/api/admin/events/${editing}` : `${API}/api/admin/events`;
    const method = editing ? "PUT" : "POST";
    const res = await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      toast.success(editing ? (lang === "es" ? "Evento actualizado" : "Event updated") : (lang === "es" ? "Evento creado" : "Event created"));
      setShowForm(false);
      setEditing(null);
      setForm(empty);
      load();
    } else {
      toast.error(lang === "es" ? "Error al guardar" : "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === "es" ? "Eliminar este evento?" : "Delete this event?")) return;
    const res = await authFetch(`${API}/api/admin/events/${id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success(lang === "es" ? "Evento eliminado" : "Event deleted");
      load();
    }
  };

  const startEdit = (ev) => {
    setForm({ title: ev.title, description: ev.description, date: ev.date, time: ev.time, location: ev.location, image_url: ev.image_url || "" });
    setEditing(ev.id);
    setShowForm(true);
  };

  const copyShareLink = (id) => {
    const url = `${window.location.origin}/events/${id}`;
    navigator.clipboard.writeText(url).then(() => toast.success(lang === "es" ? "Enlace copiado" : "Link copied!"));
  };

  if (loading) return (
    <div data-testid="admin-events-page">
      <h2 className="text-2xl font-bold text-gray-900 font-['Kanit'] mb-6">{a.events || "Events"}</h2>
      <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}</div>
    </div>
  );

  return (
    <div data-testid="admin-events-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.events || "Events"}</h2>
          <p className="text-gray-500 text-sm">{lang === "es" ? "Gestiona eventos y recaudaciones" : "Manage fundraising events"}</p>
        </div>
        <button onClick={() => { setForm(empty); setEditing(null); setShowForm(true); }} data-testid="add-event-btn"
          className="flex items-center gap-2 bg-[#87CEEB] text-black px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9] transition-colors">
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo Evento" : "New Event"}
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{editing ? (lang === "es" ? "Editar Evento" : "Edit Event") : (lang === "es" ? "Nuevo Evento" : "New Event")}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="event-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Titulo" : "Title"} *</label>
                <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Descripcion" : "Description"} *</label>
                <textarea required value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="event-desc-input"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Fecha" : "Date"} *</label>
                  <input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="event-date-input"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Hora" : "Time"} *</label>
                  <input type="text" required placeholder="e.g. 9:00 AM - 5:00 PM" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="event-time-input"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Ubicacion" : "Location"} *</label>
                <input type="text" required value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="event-location-input"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "URL de Imagen" : "Image URL"}</label>
                <input type="url" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." data-testid="event-image-input"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </button>
                <button type="submit" data-testid="event-save-btn"
                  className="flex-1 bg-[#87CEEB] text-black px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9] transition-colors">
                  {editing ? (lang === "es" ? "Actualizar" : "Update") : (lang === "es" ? "Crear" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-400">{lang === "es" ? "No hay eventos aun. Crea el primero!" : "No events yet. Create your first one!"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-sm transition-shadow" data-testid={`event-row-${ev.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {ev.image_url && (
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                    <img src={ev.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {!ev.image_url && (
                  <div className="w-16 h-16 rounded-xl bg-[#87CEEB]/10 flex items-center justify-center flex-shrink-0">
                    <Image className="w-6 h-6 text-[#87CEEB]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 font-['Kanit'] truncate">{ev.title}</h3>
                  <p className="text-sm text-gray-500 truncate">{ev.description}</p>
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {ev.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {ev.time}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => copyShareLink(ev.id)} title="Copy share link" data-testid={`share-event-${ev.id}`}
                    className="p-2 hover:bg-[#87CEEB]/10 rounded-lg transition-colors text-gray-400 hover:text-[#87CEEB]">
                    <Link2 className="w-4 h-4" />
                  </button>
                  <a href={`/events/${ev.id}`} target="_blank" rel="noopener noreferrer" title="Preview"
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => startEdit(ev)} title="Edit" data-testid={`edit-event-${ev.id}`}
                    className="p-2 hover:bg-amber-50 rounded-lg transition-colors text-gray-400 hover:text-amber-600">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} title="Delete" data-testid={`delete-event-${ev.id}`}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
